import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import rbacPermissionObject from '@/entities/rbacPermission/interface'

import {
  ALL_ROLES_QUERY,
  SINGLE_ROLE_QUERY,
  ROLES_DEPENDENTS_QUERY,
  CREATE_ROLE_MUTATION,
  UPDATE_ROLE_MUTATION,
  DELETE_ROLE_MUTATION,
  FORCE_DELETE_ROLE_MUTATION,
  ADD_ROLE_PERMISSIONS_MUTATION,
  REMOVE_ROLE_PERMISSIONS_MUTATION,
  CREATE_ROLE_PERMISSIONS_MUTATION,
  DELETE_ROLE_PERMISSIONS_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const rbacRoleRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_ROLE_QUERY, variables).then((data: any) => {
      return hydrateObject(data.role)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_ROLES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.roles)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_ROLE_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.role),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_ROLES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.roles),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(ROLES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([ROLES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_ROLE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.role)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_ROLE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.role)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_ROLE_MUTATION, variables).then((data: any) => {
      return data.deleteRbacRole
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_ROLE_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRbacRole
    })
  },

  assignPermissions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        permissionIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_ROLE_PERMISSIONS_MUTATION, variables)
  },

  unassignPermissions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        permissionIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_ROLE_PERMISSIONS_MUTATION, variables)
  },

  createPermissions: async (id: string, items: Array<rbacPermissionObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        permissions: items,
      },
    }
    return graphQLClient.request(CREATE_ROLE_PERMISSIONS_MUTATION, variables)
  },

  deletePermissions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        permissionIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_ROLE_PERMISSIONS_MUTATION, variables)
  },
}

export default rbacRoleRepository
